import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { skillAssistantApi } from '../utils/api';
import GlassCard from '../components/GlassCard';
import ChatHistorySidebar from '../components/ChatHistorySidebar';
import MarkdownMessage from '../components/MarkdownMessage';
import LoadingDots from '../components/LoadingDots';
import ModernChatInput from '../components/ModernChatInput';
import SkillConceptModal from '../components/SkillConceptModal';
import ConfirmModal from '../components/ConfirmModal';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Loader2, 
  Sparkles, 
  RefreshCw,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const SkillIdeaAssistantPage = () => {
  const { user } = useAuth();
  const { isCollapsed: mainSidebarCollapsed } = useSidebar();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [skillSpec, setSkillSpec] = useState(null);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [conversationPhase, setConversationPhase] = useState(1);
  const [conversations, setConversations] = useState([]);
  const [chatSidebarCollapsed, setChatSidebarCollapsed] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [typingMessage, setTypingMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef(null);
  const isStartingRef = useRef(false);

  // Load conversation history on mount
  useEffect(() => {
    loadConversationHistory(true); // Always skip auto-start on mount
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Extract skill spec from messages when they change
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.role === 'assistant' && lastMessage.content.includes('---SKILL_SPEC_START---')) {
      const extractedSpec = extractSkillSpec(lastMessage.content);
      if (extractedSpec) {
        setSkillSpec(extractedSpec);
        setShowSkillModal(true);
        setConversationPhase('complete');
      }
    }
  }, [messages]);
  // Force reset any stuck loading states (only if stuck for too long)
  useEffect(() => {
    const resetTimer = setTimeout(() => {
      if (isLoading) {
        console.warn('Force resetting stuck loading state after 30 seconds');
        setIsLoading(false);
      }
    }, 30000); // Reset after 30 seconds max (increased to avoid premature resets)

    return () => clearTimeout(resetTimer);
  }, [isLoading]);

  // Function to extract skill spec from assistant response
  const extractSkillSpec = (content) => {
    try {
      const startMarker = '---SKILL_SPEC_START---';
      const endMarker = '---SKILL_SPEC_END---';
      
      const startIndex = content.indexOf(startMarker);
      const endIndex = content.indexOf(endMarker);
      
      if (startIndex === -1 || endIndex === -1) {
        return null;
      }
      
      const jsonString = content.substring(startIndex + startMarker.length, endIndex).trim();
      const parsedSpec = JSON.parse(jsonString);
      
      // Add conversation metadata
      return {
        ...parsedSpec,
        discoveryConversationId: conversationId,
        discoveredAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Failed to extract skill spec:', error);
      return null;
    }
  };

  const loadConversationHistory = async (skipAutoStart = true) => {
    try {
      const response = await skillAssistantApi.getConversations();
      setConversations(response.data || []);
      setIsInitializing(false);
    } catch (error) {
      console.error('Failed to load conversation history:', error);
      setIsInitializing(false);
    }
  };

  // Typing animation function
  const animateTyping = (text, callback) => {
    setIsTyping(true);
    setTypingMessage('');
    
    const words = text.split(' ');
    let currentIndex = 0;
    
    const typeNextWord = () => {
      if (currentIndex < words.length) {
        setTypingMessage(prev => {
          const newText = prev + (prev ? ' ' : '') + words[currentIndex];
          return newText;
        });
        currentIndex++;
        setTimeout(typeNextWord, 50); // Type each word every 50ms
      } else {
        setIsTyping(false);
        setTypingMessage('');
        callback();
      }
    };
    
    typeNextWord();
  };

  const startConversation = async () => {
    // Prevent duplicate calls
    if (isStartingRef.current) {
      return;
    }
    
    isStartingRef.current = true;
    
    // Immediately show chat interface with loading state
    setIsInitializing(false);
    setIsLoading(true);
    setSkillSpec(null);
    setMessages([]);
    
    // Show typing indicator immediately
    setIsTyping(true);
    setTypingMessage('');
    
    try {
      const response = await skillAssistantApi.start();
      
      setConversationId(response.data.conversation_id);
      
      // Animate typing the first message
      animateTyping(response.data.message, () => {
        setMessages([{
          role: 'assistant',
          content: response.data.message,
          timestamp: new Date()
        }]);
      });
      
      // Reload conversation history
      await loadConversationHistory(true);
      
      // Removed toast notification - just proceed silently
    } catch (error) {
      console.error('Failed to start conversation:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to start conversation';
      
      // Reset to start state on error
      setIsTyping(false);
      setIsInitializing(true);
      setMessages([]);
    } finally {
      // Always reset loading state and starting ref
      setIsLoading(false);
      isStartingRef.current = false;
    }
  };

  const loadConversation = async (convId) => {
    setIsLoading(true);
    try {
      const response = await skillAssistantApi.getConversation(convId);
      const conv = response.data;
      
      setConversationId(conv.id);
      setMessages(conv.messages.map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      })));
      setSkillSpec(conv.skill_spec ? {
        skill_name: conv.skill_spec.skillName,
        slug: conv.skill_spec.slug,
        primary_function: conv.skill_spec.primaryFunction,
        ideal_capabilities: conv.skill_spec.idealCapabilities,
        key_differentiators: conv.skill_spec.keyDifferentiators,
        category: conv.skill_spec.category,
        ready_to_build: conv.skill_spec.readyToBuild
      } : null);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      // Always reset loading state
      setIsLoading(false);
    }
  };

  const deleteConversation = async (convId) => {
    try {
      // Optimistic update - remove from UI immediately
      setConversations(prev => prev.filter(c => c.id !== convId));
      
      // Call API to actually delete from database
      await skillAssistantApi.deleteConversation(convId);
      
      // If deleting active conversation, clear it
      if (convId === conversationId) {
        setConversationId(null);
        setMessages([]);
        setSkillSpec(null);
        setIsInitializing(true);
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      // Revert optimistic update on error
      await loadConversationHistory(true);
    }
  };

  const toggleStar = async (convId) => {
    // Optimistic update
    setConversations(prev => prev.map(conv => 
      conv.id === convId ? { ...conv, starred: !conv.starred } : conv
    ));

    try {
      await skillAssistantApi.toggleStar(convId);
    } catch (error) {
      console.error('Toggle star error:', error);
      // Revert on error
      setConversations(prev => prev.map(conv => 
        conv.id === convId ? { ...conv, starred: !conv.starred } : conv
      ));
    }
  };

  const renameConversation = async (convId, newTitle) => {
    // Optimistic update
    const oldTitle = conversations.find(c => c.id === convId)?.title;
    setConversations(prev => prev.map(conv => 
      conv.id === convId ? { ...conv, title: newTitle } : conv
    ));

    try {
      await skillAssistantApi.renameConversation(convId, newTitle);
    } catch (error) {
      console.error('Rename error:', error);
      // Revert on error
      setConversations(prev => prev.map(conv => 
        conv.id === convId ? { ...conv, title: oldTitle } : conv
      ));
    }
  };

  // Handle closing modal to refine more
  const handleCloseModal = async () => {
    setShowSkillModal(false);
    // Don't clear skillSpec - keep it so user can reopen modal
  };

  const sendMessage = async (messageText) => {
    try {
      // Get the message text
      let message = messageText || input;
      if (!message || typeof message !== 'string') {
        message = String(message || '');
      }
      
      if (!message.trim()) return;
      
      if (!conversationId) {
        await startConversation();
        return;
      }

      // Set loading FIRST to show immediate feedback
      setIsLoading(true);

      // Add user message immediately
      const userMessage = {
        role: 'user',
        content: message.trim(),
        timestamp: new Date()
      };

      setMessages(prev => [...prev, userMessage]);
      
      // Clear input if using default input
      if (!messageText) {
        setInput('');
      }

      // Get message history
      const messageHistory = [...messages, userMessage].map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Call API
      const response = await skillAssistantApi.chat(conversationId, message.trim(), messageHistory);

      // Add assistant response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date()
      }]);

      // Wait for React to render the new message before hiding loading indicator
      // Use requestAnimationFrame to wait for next paint, then add small buffer
      await new Promise(resolve => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 300); // 300ms buffer for smooth transition
        });
      });

    } catch (error) {
      console.error('Send failed:', error);
    } finally {
      // Reset loading only after message is fully rendered
      setIsLoading(false);
    }
  };

  const handleStartOver = () => {
    setShowConfirmModal(true);
  };

  const confirmStartOver = async () => {
    await startConversation();
  };

  // Parse message for button tags
  const parseMessageForButtons = (content) => {
    // First, remove SKILL_SPEC JSON markers and content
    let cleanedContent = content;
    const specStartMarker = '---SKILL_SPEC_START---';
    const specEndMarker = '---SKILL_SPEC_END---';
    
    const specStartIndex = content.indexOf(specStartMarker);
    const specEndIndex = content.indexOf(specEndMarker);
    
    if (specStartIndex !== -1 && specEndIndex !== -1) {
      // Remove everything from start marker to end marker (including markers)
      cleanedContent = content.substring(0, specStartIndex) + content.substring(specEndIndex + specEndMarker.length);
      cleanedContent = cleanedContent.trim();
    }
    
    // Now parse for buttons
    const buttonRegex = /\[BUTTONS\]([\s\S]*?)\[\/BUTTONS\]/;
    const match = cleanedContent.match(buttonRegex);

    if (!match) {
      return { beforeText: cleanedContent, hasButtons: false, options: [], question: '', afterText: '' };
    }

    const beforeText = cleanedContent.substring(0, match.index).trim();
    const afterText = cleanedContent.substring(match.index + match[0].length).trim();
    const buttonBlock = match[1].trim();
    
    const lines = buttonBlock.split('\n').filter(l => l.trim());
    const question = lines[0].trim();
    const options = lines.slice(1)
      .filter(l => l.trim().startsWith('-'))
      .map(l => l.replace(/^[-•]\s*/, '').trim());

    return {
      beforeText,
      question,
      options,
      afterText,
      hasButtons: true
    };
  };

  const handleButtonClick = async (option) => {
    // Mark the latest assistant message as answered
    setMessages(prev => prev.map((msg, index) => 
      index === prev.length - 1 && msg.role === 'assistant'
        ? { ...msg, answered: true }
        : msg
    ));

    // Send the message
    await sendMessage(option);
  };

  if (!user) {
    return (
      <div className="container mx-auto py-8 px-4">
        <GlassCard className="p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Please log in to use Skill Idea Assistant</h2>
          <Button onClick={() => navigate('/login')}>Log In</Button>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex" style={{ 
      marginLeft: mainSidebarCollapsed ? '4rem' : '16rem',
      transition: 'margin-left 300ms'
    }}>
      {/* Chat History Sidebar - Secondary Sidebar */}
      <div 
        className={`h-full transition-all duration-300 flex-shrink-0 ${
          chatSidebarCollapsed ? 'w-16' : 'w-80'
        }`}
      >
        <ChatHistorySidebar
          conversations={conversations}
          activeConversationId={conversationId}
          onSelectConversation={loadConversation}
          onNewConversation={startConversation}
          onDeleteConversation={deleteConversation}
          onRenameConversation={renameConversation}
          onToggleStar={toggleStar}
          isCollapsed={chatSidebarCollapsed}
          onToggleCollapse={() => setChatSidebarCollapsed(!chatSidebarCollapsed)}
        />
      </div>

      {/* Main Content - Full Height Flex Layout */}
      <div className="flex-1 flex flex-col bg-bg-gray dark:bg-dark-bg">
        {/* Header - Only show after conversation starts */}
        {(messages.length > 0 || isTyping || isLoading) && (
          <div className="container mx-auto px-4 py-6 max-w-5xl">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-manrope font-bold text-text-dark dark:text-dark-text">
                  Skill Idea Assistant
                </h1>
                <p className="text-lg text-text-gray dark:text-dark-text-secondary mt-1">
                  Let's discover what Claude Skill you should build
                </p>
              </div>
            <div className="flex gap-2">
              {isLoading && (
                <Button
                  onClick={() => {
                    console.log('Manual loading reset');
                    setIsLoading(false);
                  }}
                  variant="destructive"
                  size="sm"
                  className="rounded-xl text-xs"
                >
                  Reset Loading
                </Button>
              )}
              <Button
                onClick={handleStartOver}
                variant="outline"
                size="sm"
                className="rounded-xl hidden md:flex"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Start Over
              </Button>
            </div>
            </div>
          </div>
        )}

        {/* Starting State - Centered, Minimal */}
        {!isInitializing && messages.length === 0 && !isTyping && !isLoading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center justify-center transition-opacity duration-500">
              <div className="flex flex-col items-center px-4 max-w-[672px] w-full">
                {/* Heading */}
                <h1 className="text-4xl font-manrope font-bold text-text-dark dark:text-dark-text text-center mb-4">
                  Ready to discover what Claude Skill you should build?
                </h1>

                {/* Subtext */}
                <p className="text-lg text-text-gray dark:text-dark-text-secondary text-center mb-10">
                  I'll guide you to discover your perfect Claude Skill idea.
                </p>

                {/* Centered Input Box */}
                <div className="w-full mb-6">
                  <ModernChatInput
                    value={input}
                    onChange={setInput}
                    onSend={startConversation}
                    disabled={isLoading}
                    placeholder="Type a message or click Start Discovery..."
                  />
                </div>

                {/* Start Discovery Button */}
                <Button
                  onClick={startConversation}
                  disabled={isLoading}
                  variant="outline"
                  className="h-12 px-8 border-2 text-base font-semibold"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Starting Discovery...
                    </>
                  ) : (
                    'Start Discovery'
                  )}
                </Button>

                {/* Time Estimate */}
                <div className="text-center mt-12">
                  <p className="text-xs text-text-gray dark:text-dark-text-secondary">
                    Usually takes 5-10 minutes
                  </p>
                  <p className="text-xs text-text-gray dark:text-dark-text-secondary mt-2">
                    Powered by Claude Sonnet 4
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Chat Interface - Fills remaining space */}
        {(messages.length > 0 || isTyping || isLoading) && (
          <div className="flex-1 overflow-y-auto">
            <div className="container mx-auto px-4 max-w-5xl h-full">
              {/* Chat Messages - No background container */}
              <div
                ref={scrollRef}
                className="py-4 space-y-4"
              >
          {messages.map((message, index) => {
            const parsed = message.role === 'assistant' ? parseMessageForButtons(message.content) : null;
            const isLatestMessage = index === messages.length - 1;
            
            return (
              <div
                key={index}
                className={`flex ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-5 py-4 transition-all ${
                    message.role === 'user'
                      ? 'bg-primary/10 dark:bg-primary/20 text-text-dark dark:text-dark-text border border-primary/30'
                      : 'border border-gray-300 dark:border-[#2e2e2d] text-text-dark dark:text-dark-text'
                  }`}
                >
                  {message.role === 'user' ? (
                    <MarkdownMessage 
                      content={message.content} 
                      isUser={true}
                    />
                  ) : (
                    <>
                      {parsed.beforeText && (
                        <MarkdownMessage 
                          content={parsed.beforeText} 
                          isUser={false}
                        />
                      )}
                      
                      {parsed.hasButtons && (
                        <div className="mt-4 space-y-3 relative z-10">
                          <p className="font-medium text-text-dark dark:text-dark-text mb-3">
                            {parsed.question}
                          </p>
                          
                          {/* Show clickable buttons ONLY if latest message and NOT answered */}
                          {isLatestMessage && !isLoading && !message.answered ? (
                            <div className="flex flex-col gap-2">
                              {parsed.options.map((option, optIndex) => (
                                <button
                                  key={optIndex}
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleButtonClick(option);
                                  }}
                                  disabled={isLoading}
                                  className="w-full justify-start text-left py-3 px-4 rounded-lg border border-gray-300 dark:border-[#2e2e2d] hover:bg-primary/5 hover:border-primary transition-all text-text-dark dark:text-dark-text cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <span className="mr-3 text-gray-500 dark:text-gray-400 font-medium">
                                    {String.fromCharCode(65 + optIndex)})
                                  </span>
                                  {option}
                                </button>
                              ))}
                            </div>
                          ) : (
                            /* Show disabled text options if answered */
                            <div className="flex flex-col gap-2">
                              {parsed.options.map((option, optIndex) => (
                                <div
                                  key={optIndex}
                                  className="w-full text-left py-3 px-4 text-gray-500 dark:text-gray-400 opacity-60"
                                >
                                  <span className="mr-3 font-medium">
                                    {String.fromCharCode(65 + optIndex)})
                                  </span>
                                  {option}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {parsed.afterText && (
                        <div className="mt-4">
                          <MarkdownMessage 
                            content={parsed.afterText} 
                            isUser={false}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {/* Loading or Typing Animation - Always visible when loading */}
          {isLoading && (
            <div className="flex justify-start animate-fadeIn">
              <div className="border border-primary/30 dark:border-primary/40 bg-primary/5 dark:bg-primary/10 rounded-2xl px-5 py-4 max-w-[85%] shadow-sm">
                {typingMessage ? (
                  <div className="text-text-dark dark:text-dark-text">
                    {typingMessage}
                    <span className="inline-block w-1 h-4 ml-1 bg-primary animate-pulse"></span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <LoadingDots />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Thinking...
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* View Skill Concept Button - Show when spec is ready */}
          {skillSpec && !isLoading && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setShowSkillModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                Your Skill Concept is Ready! Click to View
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}
              </div>
            </div>
          </div>
        )}

        {/* Skill Concept Modal */}
        {showSkillModal && skillSpec && (
          <SkillConceptModal 
            concept={skillSpec}
            conversationId={conversationId}
            onClose={handleCloseModal}
          />
        )}

        {/* Confirm Start Over Modal */}
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={confirmStartOver}
          title="Start Over?"
          message="Are you sure you want to start a new discovery session? Your current conversation will be saved in the history sidebar."
          confirmText="Start Over"
          cancelText="Cancel"
          variant="warning"
        />
        
        {/* Input Area - Fixed at Bottom - Show when chat is active */}
        {(messages.length > 0 || isTyping || isLoading) && !isInitializing && (
          <div>
            <div className="container mx-auto px-4 py-4 max-w-5xl">
              <ModernChatInput
                value={input}
                onChange={setInput}
                onSend={sendMessage}
                disabled={isLoading}
                placeholder="Type your response..."
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillIdeaAssistantPage;
